import { useMemo, useState } from "react";
import ExecutiveMapPanel, { MAP_METRIC_OPTIONS } from "@/components/ExecutiveMapPanel";
import { DistrictLink } from "@/components/DrilldownLink";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "-");
const pct = (v) => (typeof v === "number" ? `${v}%` : "N/A%");

const formatLakhs = (total) => {
  if (!total) return "0L";
  return `${(total / 100000).toFixed(2)}L`;
};

const getMetricBadge = (district, metricKey) => {
  if (!district?.has_data) return { label: "No Data", className: "gray" };
  const value = district.metrics?.[metricKey];
  if (value == null) return { label: "No Data", className: "gray" };
  if (value >= 70) return { label: "Good", className: "" };
  if (value >= 50) return { label: "Average", className: "gray" };
  return { label: "Low", className: "red" };
};

const StatIcon = ({ children, variant }) => (
  <span className={`ed-stat-icon ${variant || ""}`}>{children}</span>
);

const ExecutiveMapViewTab = ({ mapData, sqaafOverview = null }) => {
  const [selectedDistrict, setSelectedDistrict] = useState("Pune");
  const [selectedMetric, setSelectedMetric] = useState("shi");
  const [onlyWithData, setOnlyWithData] = useState(false);
  const [sortByMetric, setSortByMetric] = useState(true);
  const [showAllNoData, setShowAllNoData] = useState(false);

  const metricMeta = MAP_METRIC_OPTIONS.find((m) => m.key === selectedMetric) || MAP_METRIC_OPTIONS[0];
  const districts = mapData?.districts || [];
  const summary = mapData?.summary || {};

  const totals = useMemo(() => {
    if (summary.total_schools != null || summary.total_students != null) {
      return {
        students: summary.total_students ?? 0,
        schools: summary.total_schools ?? 0,
      };
    }
    const withData = districts.filter((d) => d.has_data);
    return {
      students: withData.reduce((s, d) => s + (d.total_students || 0), 0),
      schools: withData.reduce((s, d) => s + (d.total_schools || 0), 0),
    };
  }, [districts, summary.total_schools, summary.total_students]);

  const insights = useMemo(() => {
    const withData = districts.filter((d) => d.has_data && d.metrics?.[selectedMetric] != null);
    if (!withData.length) {
      return { top: null, lowest: null, missing: summary.districts_no_data ?? 0 };
    }
    const sorted = [...withData].sort(
      (a, b) => (b.metrics[selectedMetric] || 0) - (a.metrics[selectedMetric] || 0),
    );
    return {
      top: sorted[0],
      lowest: sorted[sorted.length - 1],
      missing: summary.districts_no_data ?? districts.filter((d) => !d.has_data).length,
    };
  }, [districts, summary, selectedMetric]);

  const noDataRows = useMemo(() => districts.filter((d) => !d.has_data), [districts]);
  const hiddenNoDataCount = Math.max(0, noDataRows.length - 5);

  const tableRows = useMemo(() => {
    const withData = districts.filter((d) => d.has_data);
    const withoutData = showAllNoData ? noDataRows : noDataRows.slice(0, 5);

    const sortFn = (a, b) => {
      if (sortByMetric) {
        return (b.metrics?.[selectedMetric] ?? -1) - (a.metrics?.[selectedMetric] ?? -1);
      }
      return a.district_name.localeCompare(b.district_name);
    };

    if (onlyWithData) return [...withData].sort(sortFn);

    const sortedWithData = [...withData].sort(sortFn);
    const sortedWithout = [...withoutData].sort((a, b) => a.district_name.localeCompare(b.district_name));
    return [...sortedWithData, ...sortedWithout];
  }, [districts, noDataRows, onlyWithData, sortByMetric, showAllNoData, selectedMetric]);

  const resolvedSelected = districts.some((d) => d.district_name === selectedDistrict && d.has_data)
    ? selectedDistrict
    : districts.find((d) => d.has_data)?.district_name || "Pune";

  if (!mapData) {
    return (
      <div className="ed-map-view" data-testid="executive-map-view">
        <section className="ed-panel ed-map-loading-card">
          <div className="ed-map-stage ed-map-loading">Loading Maharashtra district map…</div>
          <p className="ed-map-loading-copy">
            Fetching district-level Overall School Health and coverage indicators. This view covers all Maharashtra districts.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="ed-map-view" data-testid="executive-map-view">
      <section className="ed-stat-strip" aria-label="Map view KPIs">
        <article className="ed-stat-card">
          <StatIcon>
            <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </StatIcon>
          <div>
            <strong>{summary.total_districts ?? districts.length}</strong>
            <span>Districts</span>
          </div>
        </article>
        <article className="ed-stat-card">
          <StatIcon variant="green">
            <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
          </StatIcon>
          <div>
            <strong>{summary.avg_shi != null ? `${summary.avg_shi}%` : "—"}</strong>
            <span>Average OSH</span>
          </div>
        </article>
        <article className="ed-stat-card">
          <StatIcon>
            <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
              <circle cx="12" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M2 21v-2a4 4 0 0 1 3-3.87" />
            </svg>
          </StatIcon>
          <div>
            <strong>{formatLakhs(totals.students)}</strong>
            <span>Students</span>
          </div>
        </article>
        <article className="ed-stat-card">
          <StatIcon>
            <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4v18" />
              <path d="M19 21V11l-6-4" />
            </svg>
          </StatIcon>
          <div>
            <strong>{fmt(totals.schools)}</strong>
            <span>Schools</span>
          </div>
        </article>
      </section>

      <section className="ed-map-dashboard">
        <ExecutiveMapPanel
          data={mapData}
          selectedDistrict={resolvedSelected}
          onSelectDistrict={setSelectedDistrict}
          selectedMetric={selectedMetric}
          onMetricChange={setSelectedMetric}
          sqaafOverview={sqaafOverview}
        />

        <aside className="ed-map-right-stack">
          <section className="ed-side-panel">
            <h3>Quick Insights</h3>
            <div className="ed-insight-list">
              {insights.top && (
                <article className="ed-insight-card">
                  <StatIcon variant="green">
                    <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m3 17 6-6 4 4 8-8" />
                      <path d="M14 7h7v7" />
                    </svg>
                  </StatIcon>
                  <div>
                    <small>Top Performing District</small>
                    <strong>{insights.top.district_name}</strong>
                    <span className="ed-mini-tag green">
                      {metricMeta.label} {insights.top.metrics[selectedMetric]}
                    </span>
                  </div>
                </article>
              )}
              {insights.lowest && (
                <article className="ed-insight-card">
                  <StatIcon variant="red">
                    <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m3 7 6 6 4-4 8 8" />
                      <path d="M14 17h7v-7" />
                    </svg>
                  </StatIcon>
                  <div>
                    <small>Lowest Performing District</small>
                    <strong>{insights.lowest.district_name}</strong>
                    <span className="ed-mini-tag red">
                      {metricMeta.label} {insights.lowest.metrics[selectedMetric]}
                    </span>
                  </div>
                </article>
              )}
              <article className="ed-insight-card">
                <StatIcon variant="purple">
                  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4" />
                    <path d="M12 17h.01" />
                  </svg>
                </StatIcon>
                <div>
                  <small>Districts with Missing Data</small>
                  <strong>{insights.missing} Districts</strong>
                  <span className="ed-mini-tag">Needs Review</span>
                </div>
              </article>
              <article className="ed-insight-card">
                <StatIcon>
                  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                </StatIcon>
                <div>
                  <small>Interaction Guide</small>
                  <strong>Click a district to view detailed insights.</strong>
                </div>
              </article>
            </div>
          </section>

          <section className="ed-help-card">
            <h3>
              <span className="ed-stat-icon">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                </svg>
              </span>
              What this view helps you do
            </h3>
            <div className="ed-help-list">
              <div className="ed-help-item">
                <span className="ed-help-dot">↗</span>
                <span>Compare {metricMeta.label} across all districts.</span>
              </div>
              <div className="ed-help-item">
                <span className="ed-help-dot">!</span>
                <span>Identify low-performing and missing-data regions quickly.</span>
              </div>
              <div className="ed-help-item">
                <span className="ed-help-dot">✓</span>
                <span>Prioritize interventions and track improvement.</span>
              </div>
            </div>
            <div className="ed-learn-more">
              <span>Status bands: 70+ Good · 50–69 Average · &lt;50 Low</span>
            </div>
          </section>
        </aside>
      </section>

      {districts.length > 0 && (
        <section className="ed-map-table-card">
          <div className="ed-map-table-head">
            <div>
              <h3>District-Wise Summary</h3>
              <p>
                Tabular detail for {metricMeta.label}. Map colour and status badges use the same metric and 70/50 thresholds.
              </p>
            </div>
            <div className="ed-map-table-tools">
              <button
                type="button"
                className="ed-map-filter-chip"
                onClick={() => setOnlyWithData((v) => !v)}
              >
                {onlyWithData ? "Show all ▾" : "Only with data ▾"}
              </button>
              <button
                type="button"
                className="ed-map-filter-chip"
                onClick={() => setSortByMetric((v) => !v)}
              >
                {sortByMetric ? "Sort by name ▾" : `Sort by ${metricMeta.label} ▾`}
              </button>
            </div>
          </div>
          <table aria-label="District-wise Summary" className="ed-map-table">
            <thead>
              <tr>
                <th>District</th>
                <th>Schools</th>
                <th>Students</th>
                <th>Teachers</th>
                <th>{metricMeta.label}</th>
                <th>Aadhaar %</th>
                <th>APAAR %</th>
                <th>Infra Overview</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((district) => {
                const badge = getMetricBadge(district, selectedMetric);
                const isHighlight = district.district_name === resolvedSelected && district.has_data;
                const metricValue = district.metrics?.[selectedMetric];
                return (
                  <tr
                    key={district.district_name}
                    className={isHighlight ? "ed-row-highlight" : ""}
                    onClick={() => district.has_data && setSelectedDistrict(district.district_name)}
                  >
                    <td>
                      {district.has_data && district.district_code ? (
                        <DistrictLink districtCode={district.district_code}>{district.district_name}</DistrictLink>
                      ) : (
                        district.district_name
                      )}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? fmt(district.total_schools) : "-"}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? fmt(district.total_students) : "-"}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? fmt(district.total_teachers) : "-"}
                    </td>
                    <td className={district.has_data ? "ed-score-cell" : "ed-empty"}>
                      {district.has_data ? (metricValue ?? "-") : "-"}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? pct(district.metrics?.aadhaar_coverage) : "-"}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? pct(district.metrics?.apaar_coverage) : "-"}
                    </td>
                    <td className={district.has_data ? "" : "ed-empty"}>
                      {district.has_data ? pct(district.metrics?.infrastructure_index) : "-"}
                    </td>
                    <td>
                      <span className={`ed-map-badge ${badge.className}`}>{badge.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!onlyWithData && hiddenNoDataCount > 0 && !showAllNoData && (
            <button
              type="button"
              className="ed-map-table-footer"
              onClick={() => setShowAllNoData(true)}
            >
              + {hiddenNoDataCount} more districts without data ▾
            </button>
          )}
        </section>
      )}
    </div>
  );
};

export default ExecutiveMapViewTab;
